import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import SpotCarLogo from "@/components/SpotCarLogo";
import ThemeToggle from "@/components/ThemeToggle";
import i18n from "@/lib/i18n";
import { AnalyticsPrivacyControls } from "@/components/AnalyticsConsent";

const LOCALES = [
    { value: "en", label: "EN" },
    { value: "cs", label: "CS" },
    { value: "sk", label: "SK" },
    { value: "de", label: "DE" },
    { value: "pl", label: "PL" },
    { value: "fr", label: "FR" },
    { value: "it", label: "IT" },
    { value: "es", label: "ES" },
    { value: "uk", label: "UK" },
    { value: "ru", label: "RU" },
];

export function Privacy() {
    const { t } = useTranslation();
    const privacyText = [
        { text: "SpotCar Labs respects your privacy. This policy explains what data we collect, how we use it, and your rights under the GDPR." },
        { heading: "1. Data we collect" },
        { text: "Account: email, name, country and language preference (via Clerk). Usage: car listing URLs you analyse, AI-generated reports, your notes, collections, and feedback." },
        { text: "Payments: Stripe processes payments. We never see or store your card details." },
        { heading: "2. How we use data" },
        { text: "To provide the service (running analyses, history, collections, sharing), bill you fairly, prevent abuse, and improve product quality. We do not sell your data." },
        { heading: "3. Sharing & sub-processors" },
        { text: "Clerk (auth), Stripe (payments), Redis (job queue) and our hosting providers in the EU. AI inference runs on infrastructure compliant with GDPR." },
        { heading: "4. Your rights" },
        { text: "Access, export (Settings → GDPR export), correction, deletion (Settings → Delete account), and the right to lodge a complaint with your supervisory authority." },
        { heading: "5. Retention" },
        { text: "Analyses are retained as long as your account is active. Deleted accounts are removed within 30 days." },
        { heading: "6. Contact" },
        { text: "Email info@spotcar.io for privacy requests." },
        { heading: t("analytics.privacyHeading") },
        { text: t("analytics.privacyText") },
    ];
    return <Legal title="Privacy Policy" body={privacyText} showAnalyticsControls />;
}

export function Terms() {
    return <Legal title="Terms of Service" body={termsText} />;
}

function Legal({ title, body, showAnalyticsControls = false }) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-[var(--surface)] font-body text-[var(--text)]">
            <header className="sticky top-0 z-50 border-b-2 border-[var(--text)] bg-[var(--surface-container)]">
                <div className="flex items-stretch">
                    {/* Back button */}
                    <button
                        onClick={() => navigate(-1)}
                        className="flex min-h-11 min-w-11 shrink-0 items-center border-r-2 border-[var(--text)] bg-transparent px-3 py-4 text-inherit transition-colors duration-100 hover:bg-[var(--surface-container-highest)] focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)] sm:px-5"
                        aria-label={t("common.back", "Back")}
                    >
                        <ArrowLeft size={20} />
                    </button>

                    {/* Logo */}
                    <Link
                        to="/"
                        className="flex min-h-11 min-w-11 shrink-0 items-center gap-3 border-r-2 border-[var(--text)] px-1.5 py-4 transition-colors duration-100 hover:bg-[var(--surface-container-highest)] focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)] sm:px-6"
                    >
                        <SpotCarLogo size={32} />
                        <span className="hidden font-display text-xl uppercase tracking-tighter sm:inline">
                            SpotCar
                        </span>
                    </Link>

                    {/* Right side controls */}
                    <nav className="ml-auto flex shrink-0 items-stretch">
                        <div className="flex self-stretch items-center border-l-2 border-[var(--text)] [&>button]:min-w-11 [&>button]:px-3 sm:[&>button]:px-6">
                            <ThemeToggle variant="nav" />
                        </div>
                        <div className="relative flex self-stretch items-center border-l-2 border-[var(--text)]">
                            <select
                                value={i18n.language}
                                onChange={(e) => {
                                    i18n.changeLanguage(e.target.value);
                                    localStorage.setItem("spotcar-locale", e.target.value);
                                }}
                                aria-label={t("common.language", "Language")}
                                className="min-h-11 min-w-11 self-stretch appearance-none bg-transparent pl-3 pr-7 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-100 hover:bg-[var(--surface-container-highest)] focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)] sm:pl-6 sm:pr-10"
                            >
                                {LOCALES.map((l) => (
                                    <option key={l.value} value={l.value}>{l.label}</option>
                                ))}
                            </select>
                            <svg className="pointer-events-none absolute right-2 h-3 w-3 sm:right-4" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5l3-3 3 3" /><path d="M3 7l3 3 3-3" /></svg>
                        </div>
                    </nav>
                </div>
            </header>
            <article className="max-w-[760px] mx-auto p-6 md:p-12">
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--text-muted)]">
                    ── Legal · last updated 2026-07-03
                </span>
                <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase tracking-tight">
                    {title}
                </h1>
                <div
                    className="mt-8 max-w-none space-y-4 text-sm leading-relaxed text-[var(--text)]"
                    data-testid="legal-body"
                >
                    {body.map((p, i) =>
                        p.heading ? (
                            <h2
                                key={i}
                                className="font-display text-xl uppercase tracking-tight mt-8 mb-2"
                            >
                                {p.heading}
                            </h2>
                        ) : (
                            <p key={i}>{p.text}</p>
                        ),
                    )}
                </div>
                {showAnalyticsControls && <AnalyticsPrivacyControls />}
                <div className="mt-12 border-t-2 border-[var(--text)] pt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                    ── Questions? info@spotcar.io
                </div>
            </article>
        </div>
    );
}

const termsText = [
    { text: "By using SpotCar you agree to these Terms. SpotCar provides AI-assisted analysis of used car listings. We do not guarantee accuracy and are not responsible for purchasing decisions you make." },
    { heading: "1. Service description" },
    { text: "The Score, verdict, reliability notes, market price comparison, and other report sections are informational tools. Always perform a physical inspection before purchasing a vehicle." },
    { heading: "2. Credits" },
    { text: "Credits are pre-paid. 1 analysis = 1 credit. Credits do not expire. Cached results do not cost a credit. Refunds available within 14 days for unused credits." },
    { heading: "3. Acceptable use" },
    { text: "No scraping, no abuse of rate limits, no submitting illegal content. We may suspend accounts that violate these rules." },
    { heading: "4. Liability" },
    { text: "Service provided as-is. To the maximum extent permitted by law, SpotCar Labs is not liable for damages exceeding the amount you paid in the past 12 months." },
    { heading: "5. Changes" },
    { text: "We may update these terms. Material changes will be announced in-app. Continued use after notice constitutes acceptance." },
    { heading: "6. Governing law" },
    { text: "Czech Republic, with disputes settled in courts of Prague unless local consumer protection law applies." },
];
